// Stock count, shown in place of the style grid: pick a style and colour (or
// just scan), count what is on the shelf by size, review it against the
// system's stock, and save it for the back office to confirm. Online only: the
// expected figures and the saved count live on the server. The count is blind
// until Review, so the expected stock doesn't steer what staff find.
// One box finds what to count: a style name, an item code with or without its
// dashes, or a SKU/barcode. Enter counts one piece when a full code matches one size;
// otherwise the matching styles and sizes are listed to pick from.
import React, { useEffect, useMemo, useReducer, useRef, useState } from "react";
import dayjs from "dayjs";
import "./FashionWorkPanels.css";
import "./FashionCountPanel.css";
import db from "../../../database";
import { fashionPosService } from "../../../services";
import { findVariant, readVariantAxes } from "../../../utils/fashion/styleIndex";
import { productForCode } from "../../../utils/fashion/markdowns";
import { readMessage } from "../../../utils/fashion/holds";
import {
  buildStockOnHandQuery,
  buildSubmitCountMutation,
  countedPieces,
  countLines,
  countReducer,
  countTarget,
  emptyCount,
  hasTag,
  isFull,
  isRfidCode,
  isStaleDraft,
  loadCountDraft,
  MAX_COUNT_LINES,
  matchCountProducts,
  matchCountStyles,
  readStockOnHand,
  resolveCountScan,
  saveCountDraft,
  taggedPieces,
  varianceRows,
} from "../../../utils/fashion/stockCount";
import useWedgeScanner from "./hooks/useWedgeScanner";
import useFashionToast, { Toast } from "./hooks/useFashionToast";
import EmptyState from "./EmptyState";

const STYLE_RESULTS = 8;
const SIZE_RESULTS = 8;
// Readers differ in letter case, so try the tag as read, then upper and lower.
// db.rfidData.get works the same on Dexie and SQLite.
export async function lookupTag(tag) {
  for (const t of [...new Set([tag, tag.toUpperCase(), tag.toLowerCase()])]) {
    const row = await db.rfidData.get(t);
    if (row) return row;
  }
  return null;
}

export default function FashionCountPanel({ tillData, isOnline, styles, products, productById, onClose, backLabel = "Back to styles" }) {
  const bunitId = tillData?.tillAccess?.csBunit?.csBunitId;
  const online = typeof isOnline === "boolean" ? isOnline : typeof navigator === "undefined" || navigator.onLine;
  const [count, dispatch] = useReducer(countReducer, bunitId, (id) => loadCountDraft(id) || emptyCount());
  const [term, setTerm] = useState("");
  const [style, setStyle] = useState(null);
  const [note, setNote] = useState("");
  const [toast, say] = useFashionToast();
  const [review, setReview] = useState(null); // { levels } once Review has loaded
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false); // state lags a fast double tap; the ref doesn't
  const countRef = useRef(count);
  countRef.current = count;

  useEffect(() => saveCountDraft(bunitId, count), [bunitId, count]);

  const styleHits = useMemo(() => matchCountStyles(styles, term), [styles, term]);
  const sizeMatch = useMemo(() => matchCountProducts(products, term), [products, term]);

  const nameOf = (id) => productById?.(id)?.name || id;
  const skuOf = (id) => productById?.(id)?.value || "";
  const variantOf = (id) => {
    const p = productById?.(id);
    if (!p) return "";
    const { colour, size } = readVariantAxes(p);
    return [colour, size].filter((v) => v != null && v !== "").join(" · ");
  };
  const fullMsg = `A count holds up to ${MAX_COUNT_LINES} sizes. Save this one and start another for the rest.`;

  // Any change to the count after Review means the review is out of date.
  const change = (action) => {
    setReview(null);
    dispatch(action);
  };

  const pickColour = (colour) => {
    const list = style.sizes.length > 0 ? style.sizes : [null];
    const ids = list.map((s) => findVariant(style, colour, s)?.mProductId).filter(Boolean);
    const added = ids.filter((id) => !count.order.includes(id));
    change({ type: "include", productIds: ids });
    if (count.order.length + added.length > MAX_COUNT_LINES) say(fullMsg, "warn");
    else say(`${style.name}${colour ? ` ${colour}` : ""}: count each size, or scan the pieces.`, "info");
    setStyle(null);
    setTerm("");
  };

  // One more piece of a size (tag set for an RFID read).
  const countOne = (hit) => {
    const before = countRef.current;
    if (hit.tag && hasTag(before, hit.tag)) {
      say(`That tag is already counted (${nameOf(hit.productId)}).`, "info");
      return;
    }
    if (!before.order.includes(hit.productId) && isFull(before)) {
      say(fullMsg, "warn");
      return;
    }
    change(hit.tag ? { type: "tag", tag: hit.tag, productId: hit.productId } : { type: "add", productId: hit.productId });
    const had = before.qty[hit.productId] || 0;
    const tagged = taggedPieces(before, hit.productId);
    const mixed = hit.tag ? had > tagged : tagged > 0;
    if (mixed) say(`Counted ${nameOf(hit.productId)}: ${had + 1}. This size has both tag reads and barcode scans, so check no piece was counted both ways.`, "warn");
    else say(`Counted ${nameOf(hit.productId)}: ${had + 1}.`, "ok");
  };

  const onScan = async (raw) => {
    const hit = await resolveCountScan(raw, products, productForCode, lookupTag);
    if (!hit) {
      say(`Nothing on this till matches ${String(raw).trim()}.`, "warn");
      return;
    }
    countOne(hit);
  };

  useWedgeScanner(onScan, { enabled: online && !review && !busy });

  // Enter in the search box: a size whose full code, SKU or barcode was typed is
  // counted at once; an RFID tag or a code nothing matches goes through the scan
  // lookup; name or part-code matches are listed to pick from.
  const submitSearch = (e) => {
    e.preventDefault();
    const c = term.trim();
    if (!c) return;
    const target = isRfidCode(c) ? null : countTarget(sizeMatch);
    if (target) {
      setTerm("");
      setStyle(null);
      countOne({ productId: target.mProductId, tag: null });
    } else if (isRfidCode(c) || sizeMatch.hits.length === 0) {
      setTerm("");
      setStyle(null);
      onScan(c);
    } else if (sizeMatch.hits.length === 1) say(`1 size matches “${c}”. Tap it to count, or scan its code.`, "info");
    else say(`${sizeMatch.hits.length} sizes match “${c}”. Pick the one you're counting.`, "info");
  };

  const pickSize = (p) => {
    setTerm("");
    setStyle(null);
    countOne({ productId: p.mProductId, tag: null });
  };

  const startReview = async () => {
    if (count.order.length === 0 || busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    say("");
    try {
      const res = readStockOnHand(await fashionPosService(buildStockOnHandQuery(bunitId, count.order)));
      if (res.error) say(res.error, "bad");
      else setReview({ levels: res.levels });
    } catch {
      say("Couldn't read the stock on hand. Check the connection and try again.", "bad");
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const save = async () => {
    if (busyRef.current || count.order.length === 0) return;
    busyRef.current = true;
    setBusy(true);
    try {
      const res = readMessage(await fashionPosService(buildSubmitCountMutation(bunitId, note, countLines(count), count.countId)), "submitFashionStockCount", "Couldn't save the count. Try again.");
      if (res.ok) {
        say(`${res.message || "Count saved."}${res.documentNo ? ` Reference ${res.documentNo}.` : ""}`, "ok");
        setReview(null);
        setNote("");
        dispatch({ type: "reset" });
      } else say(res.message, "bad");
    } catch {
      say("Couldn't save the count. Check the connection and try again.", "bad");
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const discard = () => {
    setReview(null);
    setNote("");
    dispatch({ type: "reset" });
    say("Count cleared.", "info");
  };

  const rows = review ? varianceRows(count, review.levels) : null;
  const differing = rows ? rows.filter((r) => r.diff !== 0).length : 0;
  const linesDone = count.order.filter((id) => (count.qty[id] || 0) > 0).length;
  const pct = count.order.length ? Math.round((linesDone / count.order.length) * 100) : 0;
  const unitsExpected = rows ? rows.reduce((n, r) => n + (r.expected || 0), 0) : null;
  const varTone = (d) => (d == null ? "is-unknown" : d === 0 ? "is-ok" : d > 0 ? "is-up" : "is-down");
  const q = term.trim();
  const sizeHits = sizeMatch.hits.slice(0, SIZE_RESULTS);
  const moreSizes = sizeMatch.hits.length - sizeHits.length;

  return (
    <section className="fp-panel fp-cnt" aria-label="Stock count">
      <div className="fp-panel-head">
        <h2 className="fp-panel-title">{review ? "Review count" : "Stock count"}</h2>
        <div className="fp-row">
          {count.order.length > 0 && !review && (
            <button type="button" className="fp-btn-ghost fp-btn-sm" onClick={discard} disabled={busy}>
              Clear count
            </button>
          )}
          <button type="button" className="fp-btn-ghost fp-btn-sm" onClick={onClose}>
            {backLabel}
          </button>
        </div>
      </div>
      {!online ? (
        <EmptyState icon="offline" title="You're offline" hint="Stock counts need a connection, because the system's stock and the saved count are on the server. Reconnect the till and try again." />
      ) : (
        <>
          <Toast toast={toast} />
          {count.order.length > 0 && Number(count.startedAt) > 0 && (
            <p className="fp-wp-meta fp-num fp-cnt-meta">Started {dayjs(Number(count.startedAt)).format("HH:mm")} · cycle count</p>
          )}
          {isStaleDraft(count) && <p className="fp-wp-meta is-bad fp-cnt-stale" role="alert">This count was started more than 12 hours ago and stock has moved since. Check it again, or clear it and start a fresh one.</p>}
          {!review && (
            <>
              <form className="fp-cnt-bar" onSubmit={submitSearch} aria-label="Scan to count">
                <label className="fp-cnt-search">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <circle cx="11" cy="11" r="7" />
                    <path d="M20 20l-3.5-3.5" />
                  </svg>
                  <input
                    className="fp-num"
                    value={term}
                    onChange={(e) => {
                      setTerm(e.target.value);
                      setStyle(null);
                    }}
                    placeholder="Scan, or type a style name, item code or SKU"
                    aria-label="Search or scan"
                    autoComplete="off"
                  />
                </label>
                <button type="submit" className="fp-btn-primary fp-btn-sm">
                  Count
                </button>
              </form>
              {style ? (
                <div className="fp-cnt-pick" role="group" aria-label={`Colours of ${style.name}`}>
                  {(style.colours.length > 0 ? style.colours : [null]).map((c) => (
                    <button key={c ?? "-"} type="button" className="fp-chip" onClick={() => pickColour(c)}>
                      {c ?? "All sizes"}
                    </button>
                  ))}
                </div>
              ) : (
                q &&
                (styleHits.length > 0 || sizeHits.length > 0) && (
                  <div className="fp-cnt-matches">
                    {styleHits.length > 0 && (
                      <div className="fp-cnt-pick" role="group" aria-label="Styles">
                        <span className="fp-wp-lbl">Put a style's sizes on the sheet</span>
                        {styleHits.slice(0, STYLE_RESULTS).map((s) => (
                          <button key={s.styleId} type="button" className="fp-chip" onClick={() => setStyle(s)}>
                            {s.name}
                          </button>
                        ))}
                      </div>
                    )}
                    {sizeHits.length > 0 && (
                      <ul className="fp-cnt-sizes" aria-label="Matching sizes">
                        {sizeHits.map((p) => (
                          <li key={p.mProductId}>
                            <button type="button" className="fp-cnt-size" onClick={() => pickSize(p)} aria-label={`Count one ${p.name}`}>
                              <span className="fp-cnt-size-name">{p.name}</span>
                              <span className="fp-wp-meta fp-num">{[p.value, p.upc].filter(Boolean).join(" · ")}</span>
                              <span className="fp-cnt-add" aria-hidden="true">
                                +1
                              </span>
                            </button>
                          </li>
                        ))}
                        {moreSizes > 0 && <li className="fp-wp-meta">{moreSizes} more. Keep typing to narrow it down.</li>}
                      </ul>
                    )}
                  </div>
                )
              )}
              {q && styleHits.length === 0 && sizeHits.length === 0 && !isRfidCode(q) && (
                <p className="fp-wp-meta">No style, item code or SKU matches “{q}”. Press Enter to look it up as a tag.</p>
              )}
            </>
          )}

          <div className="fp-cnt-layout">
          <div className="fp-cnt-main">
          {count.order.length === 0 ? (
            <EmptyState icon="search" title="The sheet is empty" hint="Find a style and pick a colour to put its sizes on the sheet, or scan pieces straight away. RFID tags count once each." />
          ) : review ? (
            <>
              <p className="fp-muted">
                {differing === 0 ? "Everything counted matches the system." : `${differing} of ${rows.length} sizes differ.`} Sizes you don't count are left as they are.
              </p>
              <div className="fp-cnt-tablewrap">
                <table className="fp-cnt-table" aria-label="Counted against expected">
                  <thead>
                    <tr>
                      <th scope="col">Style</th>
                      <th scope="col">Colour · size</th>
                      <th scope="col">SKU</th>
                      <th scope="col" className="fp-num">Expected</th>
                      <th scope="col" className="fp-num">Counted</th>
                      <th scope="col" className="fp-num">Variance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.productId}>
                        <td className="fp-cnt-name">{nameOf(r.productId)}</td>
                        <td>{variantOf(r.productId) || "—"}</td>
                        <td className="fp-num fp-muted">{skuOf(r.productId) || "—"}</td>
                        <td className="fp-num">{r.expected ?? "unknown"}</td>
                        <td className="fp-num">{r.counted}</td>
                        <td className="fp-num">
                          <span className={`fp-cnt-var ${varTone(r.diff)}`}>{r.diff == null ? "?" : r.diff > 0 ? `+${r.diff}` : r.diff === 0 ? "OK" : r.diff}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <label className="fp-field">
                <span className="fp-lbl fp-muted">Note for the back office (optional)</span>
                <input className="fp-input" value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} />
              </label>
              <div className="fp-row">
                <button type="button" className="fp-btn-ghost" onClick={() => setReview(null)} disabled={busy}>
                  Back to counting
                </button>
                <button type="button" className="fp-btn-primary" onClick={save} disabled={busy}>
                  {busy ? "Saving…" : "Save count"}
                </button>
              </div>
            </>
          ) : (
            <>
              <ul className="fp-panel-list" aria-label="Count sheet">
                {count.order.map((id) => (
                  <li key={id} className="fp-panel-row">
                    <span className="fp-panel-main">
                      <span className="fp-panel-doc">{nameOf(id)}</span>
                      <span className="fp-muted fp-num">{[variantOf(id), skuOf(id)].filter(Boolean).join(" · ")}</span>
                    </span>
                    <span className="fp-qty">
                      <button type="button" className="fp-qty-btn" aria-label={`One less ${nameOf(id)}`} onClick={() => change({ type: "add", productId: id, by: -1 })} disabled={!count.qty[id]}>
                        −
                      </button>
                      <input
                        className="fp-input fp-qty-input fp-num"
                        value={count.qty[id] ?? 0}
                        onChange={(e) => change({ type: "set", productId: id, qty: e.target.value === "" ? 0 : e.target.value })}
                        inputMode="numeric"
                        aria-label={`Counted ${nameOf(id)}`}
                      />
                      <button type="button" className="fp-qty-btn" aria-label={`One more ${nameOf(id)}`} onClick={() => change({ type: "add", productId: id })}>
                        +
                      </button>
                    </span>
                    <button type="button" className="fp-btn-ghost fp-btn-sm" onClick={() => change({ type: "remove", productId: id })}>
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
              <div className="fp-row">
                <span className="fp-muted fp-num" role="status">
                  {count.order.length} sizes, {countedPieces(count)} pieces counted
                </span>
                <button type="button" className="fp-btn-primary" onClick={startReview} disabled={busy}>
                  {busy ? "Checking…" : "Review count"}
                </button>
              </div>
            </>
          )}
          </div>
          {count.order.length > 0 && (
            <aside className="fp-cnt-side" aria-label="Count summary">
              <div className="fp-cnt-stat">
                <span className="fp-wp-lbl">Lines counted</span>
                <span className="fp-num fp-cnt-big">
                  {linesDone} of {count.order.length} · {pct}%
                </span>
                <span className="fp-cnt-bar-track" aria-hidden="true">
                  <span className="fp-cnt-bar-fill" style={{ width: `${pct}%` }} />
                </span>
              </div>
              <div className="fp-cnt-stat">
                <span className="fp-wp-lbl">Units</span>
                <span className="fp-num fp-cnt-big">{countedPieces(count)}</span>
                {unitsExpected != null && <span className="fp-muted fp-num">{unitsExpected} expected</span>}
              </div>
              {rows && (
                <div className="fp-cnt-stat">
                  <span className="fp-wp-lbl">With variance</span>
                  <span className={`fp-num fp-cnt-big${differing ? " is-warn" : ""}`}>{differing}</span>
                </div>
              )}
              {!rows && <p className="fp-muted fp-cnt-blind">Expected stock stays hidden until Review, so it doesn't steer the count.</p>}
            </aside>
          )}
          </div>
        </>
      )}
    </section>
  );
}
